import getFileKey from './getFileKey'
import getFileDimensions, { RESIZED_FILE_TYPES, type S3UrlComponents } from './getFileDimensions'
import getPageCount from './getPageCount'
import getUploadData from './getUploadData'
import compressFile, { type CompressFileOptions, type CompressFallbackReason } from './compressFile'
import generateVideoThumbnail from './generateVideoThumbnail'
import getRequiredTask from './getRequiredTask'
import getUploadUrl from './getUploadUrl'
import getMp4ThumbnailKey from './getMp4ThumbnailKey'
import dataUriToBlob from './dataUriToBlob'
import getInitialImageWidth from './getInitialImageWidth'
import shouldTranscodeVideo, { ISOBMFF_VIDEO_TYPES, type VideoTranscodeDecision } from './shouldTranscodeVideo'
import {
  clearVideoTranscodePending,
  getVideoTranscodePending,
  markVideoTranscodePending,
  VIDEO_TRANSCODE_WATCH_TIMEOUT_MS,
  type PendingVideoTranscode
} from './videoTranscodePending'
import detectVideoCodec, {
  HEVC_VIDEO_CODECS,
  isHevcCodec,
  isPlayableVideoCodec,
  needsTranscode,
  PLAYABLE_VIDEO_CODECS,
  type VideoByteSource,
  type VideoCodecInfo
} from './detectVideoCodec'

export type {
  CompressFileOptions,
  CompressFallbackReason,
  PendingVideoTranscode,
  S3UrlComponents,
  VideoByteSource,
  VideoCodecInfo,
  VideoTranscodeDecision
}

const PERMITTED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/svg+xml',
  // 'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
  'video/*'
]

export function isFilePdf(file: { format?: string; type?: string; src?: string }): boolean {
  return (
    file?.format === 'pdf' || file?.type === 'application/pdf' || (!!file?.src && /\.pdf(?:\?.*)?$/i.test(file.src))
  )
}

// Extensions we treat as video. Mirrors VIDEO_FORMATS in
// @paintscout/ui FileViewer/file-type-utils.ts so the list thumbnail and the
// media viewer agree on what counts as a video.
const VIDEO_FORMATS = ['mp4', 'mov', 'webm', 'm4v', 'ogv', 'avi', 'mkv']

/**
 * Whether a file is a video. Uploaded videos (mp4/mov) go through Cloudinary
 * (see uploadFile.ts) and carry `format` (e.g. 'mp4') + a `src` secure_url, so we
 * match on the format first, then a known extension, then the extension on `src`.
 * Kept beside isFilePdf so callers (e.g. FilesDocList) can decide how to render a
 * file without reaching into ui internals.
 */
export function isFileVideo(file: { format?: string; type?: string; src?: string }): boolean {
  if (!file) return false
  if (file.format?.startsWith('video') || file.type?.startsWith('video')) return true
  if (file.format && VIDEO_FORMATS.includes(file.format.toLowerCase())) return true
  if (!file.src) return false
  const ext = file.src.split('?')[0]?.split('.').pop()?.toLowerCase() ?? ''
  return VIDEO_FORMATS.includes(ext)
}

// Mirrors IMAGE_FORMATS in @paintscout/ui FileViewer/file-type-utils.ts so the list thumbnail
// and the media viewer agree on what counts as an image.
const IMAGE_FORMATS = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'heic', 'avif', 'bmp']

/**
 * Whether a file is an image. Kept beside isFilePdf/isFileVideo so callers (e.g.
 * InlineEditableMedia) can decide how to render a file without reaching into ui internals.
 */
export function isFileImage(file: { format?: string; type?: string; src?: string }): boolean {
  if (!file) return false
  if (file.format?.startsWith('image') || file.type?.startsWith('image')) return true
  if (file.format && IMAGE_FORMATS.includes(file.format.toLowerCase())) return true
  if (!file.src) return false
  const ext = file.src.split('?')[0]?.split('.').pop()?.toLowerCase() ?? ''
  return IMAGE_FORMATS.includes(ext)
}

export interface S3FileUploadData {
  size: number
  src: string
  s3PublicKey: string
  bucket: string
  format: string
  type: string
  pages?: number
  width?: number
  height?: number
}

export {
  PERMITTED_FILE_TYPES,
  RESIZED_FILE_TYPES,
  VIDEO_TRANSCODE_WATCH_TIMEOUT_MS,
  HEVC_VIDEO_CODECS,
  ISOBMFF_VIDEO_TYPES,
  PLAYABLE_VIDEO_CODECS,
  compressFile,
  detectVideoCodec,
  isHevcCodec,
  isPlayableVideoCodec,
  needsTranscode,
  shouldTranscodeVideo,
  clearVideoTranscodePending,
  getVideoTranscodePending,
  markVideoTranscodePending,
  getFileKey,
  getUploadData,
  getPageCount,
  getFileDimensions,
  dataUriToBlob,
  getMp4ThumbnailKey,
  getUploadUrl,
  getRequiredTask,
  generateVideoThumbnail,
  getInitialImageWidth
}
